import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";

import ClientSpreadsheetService from "../../../../../services/clientSpreadsheet.service";
import {
  AvailableField,
  FieldDomain,
  FieldMapping,
  FieldMappingPayload,
  fieldHasStageValues,
  MAPPING_VALUE_STAGE_LABEL,
  MappingSource,
  MappingType,
  MappingValueStage,
  normalizeFieldMapping,
  REPEATS_GROUP_FILTER_MESSAGE,
  repeatsGroupFilter,
  SheetInspection,
  SpreadsheetCategory,
} from "../../../../../types/clientSpreadsheet.type";
import { BudgetItemType } from "../../../../../types/budget/budget";

const BUDGET_CATEGORY_DOMAINS: FieldDomain[] = [
  "budget",
  "budgetService",
  "rsvp",
];

/** Campos que ja representam a categoria; nao exigem filtro categoria no mapeamento */
const CATEGORY_FIELD_PATHS = new Set(["categoria", "type"]);

function fieldNeedsCategory(field: AvailableField | undefined): boolean {
  if (!field) return false;
  if (CATEGORY_FIELD_PATHS.has(field.path)) return false;
  if (field.requiresCategory) return true;
  return BUDGET_CATEGORY_DOMAINS.includes(field.domain);
}

/**
 * Letras das colunas cujo cabecalho, na linha informada, e o texto digitado.
 * So da para saber na linha que a inspecao leu; em outra linha, devolve vazio.
 */
export function findRepeatedHeaderLetters(
  sheets: SheetInspection[],
  sheetName: string | undefined,
  headerRow: number | undefined,
  header: string | undefined
): string[] {
  const wanted = header?.trim().toLowerCase();
  if (!wanted) return [];

  const sheet = sheets.find((s) => s.name === sheetName);
  if (!sheet || sheet.headerRow !== (headerRow ?? 1)) return [];

  return sheet.headers
    .filter((h) => h.value.trim().toLowerCase() === wanted)
    .map((h) => h.letter);
}

type Props = {
  open: boolean;
  initialData: Partial<FieldMappingPayload> | null;
  sheets: SheetInspection[];
  onConfirm: (mapping: FieldMapping) => void;
  onClose: () => void;
};

const EMPTY: Partial<FieldMapping> = {
  label: "",
  sheetName: "",
  type: "cell",
  source: "context",
  cellRef: "",
  columnHeader: "",
  columnLetter: "",
  arrayField: "",
  headerRow: 1,
  startRow: 2,
  ourField: "",
  category: undefined,
  group: "",
};

export default function MappingDialog({
  open,
  initialData,
  sheets,
  onConfirm,
  onClose,
}: Props) {
  const [form, setForm] = useState<Partial<FieldMapping>>(EMPTY);

  useEffect(() => {
    setForm(initialData ? normalizeFieldMapping(initialData) : EMPTY);
  }, [initialData, open]);

  const { data: availableFields = [] } = useQuery<AvailableField[]>({
    queryKey: ["spreadsheet-fields"],
    queryFn: () => ClientSpreadsheetService.getFields(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const { data: categories = [], isLoading: loadingCategories } = useQuery<
    SpreadsheetCategory[]
  >({
    queryKey: ["spreadsheet-categories"],
    queryFn: () => ClientSpreadsheetService.getCategories(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const categoryOptions = useMemo(
    () =>
      [...categories].sort((a, b) =>
        a.categoryLabel.localeCompare(b.categoryLabel, "pt-BR")
      ),
    [categories]
  );

  const filteredFields = availableFields.filter(
    (f) => f.source === form.source
  );

  const selectedField = availableFields.find((f) => f.path === form.ourField);
  const showCategory = fieldNeedsCategory(selectedField);
  // Celula + Array escreve so o 1o item do recorte. Foi assim que o bloco de
  // um agrupamento saiu com o NOME do agrupamento numa celula, em vez dos
  // servicos: a combinacao e valida, mas quase nunca e o que se quer.
  const writesOnlyFirstItem =
    form.type === "cell" && form.source === "arrayItem";
  // Diferente do aviso acima, este bloqueia: a geracao ignora o campo, entao
  // salvar so produz um bloco vazio sem explicacao.
  const repeatsFilter = repeatsGroupFilter(form);
  // Com o recorte por agrupamento, a secao do template e do AGRUPAMENTO, e ele
  // costuma misturar categorias. Obrigar a categoria forcava um conjunto de
  // colunas por categoria para a mesma secao; sem ela, um conjunto so traz
  // todas.
  const hasGroupFilter =
    form.source === "arrayItem" && Boolean(form.group?.trim());
  const categoryRequired = showCategory && !hasGroupFilter;

  // O mesmo texto em mais de uma coluna da linha de cabecalho — no PME do
  // SOMOS, "Qtdd" esta em E, J e O. Sem a letra o backend usa a ULTIMA, e o
  // orcamento inicial (E) nunca e preenchido.
  const repeatedHeaderLetters = useMemo(
    () =>
      form.type === "column"
        ? findRepeatedHeaderLetters(
            sheets,
            form.sheetName,
            form.headerRow,
            form.columnHeader
          )
        : [],
    [sheets, form.type, form.sheetName, form.headerRow, form.columnHeader]
  );
  const columnLetter = form.columnLetter?.trim().toUpperCase() ?? "";
  const letterIsValid = !columnLetter || /^[A-Z]{1,3}$/.test(columnLetter);
  const needsColumnLetter =
    repeatedHeaderLetters.length > 1 &&
    !repeatedHeaderLetters.includes(columnLetter);

  const showValueStage =
    form.source === "arrayItem" && fieldHasStageValues(form.ourField);

  function set(key: keyof FieldMapping, value: any) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSourceChange(value: MappingSource) {
    setForm((prev) => ({
      ...prev,
      source: value,
      ourField: "",
      arrayField: "",
      category: undefined,
      group: "",
    }));
  }

  function handleTypeChange(value: MappingType) {
    setForm((prev) => ({
      ...prev,
      type: value,
      cellRef: "",
      columnHeader: "",
      columnLetter: "",
      headerRow: 1,
      startRow: 2,
      arrayField: prev.source === "arrayItem" ? prev.arrayField : "",
    }));
  }

  function isValid(): boolean {
    if (!form.sheetName || !form.ourField || !form.source || !form.type)
      return false;
    if (form.type === "cell" && !form.cellRef) return false;
    if (form.type === "column" && !form.columnHeader) return false;
    if (form.type === "column" && (!letterIsValid || needsColumnLetter))
      return false;
    if (form.source === "arrayItem" && !form.arrayField) return false;
    if (categoryRequired && !form.category) return false;
    if (repeatsFilter) return false;
    return true;
  }

  function handleConfirm() {
    if (!isValid()) return;
    const mapping = { ...(form as FieldMapping) };
    if (!showCategory || !mapping.category) {
      delete mapping.category;
    }
    if (!showValueStage || !mapping.valueStage) {
      delete mapping.valueStage;
    }
    if (form.type !== "column" || !columnLetter) {
      delete mapping.columnLetter;
    } else {
      mapping.columnLetter = columnLetter;
    }
    // Filtro so existe para bloco de array; string vazia significa "sem
    // recorte" e nao deve ir para o banco.
    const group = mapping.group?.trim();
    if (form.source !== "arrayItem" || !group) {
      delete mapping.group;
    } else {
      mapping.group = group;
    }
    onConfirm(mapping);
  }

  const uniqueArrayFields = Array.from(
    new Set(
      availableFields
        .filter((f) => f.source === "arrayItem" && f.arrayField)
        .map((f) => f.arrayField as string)
    )
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData?.ourField ? "Editar Mapeamento" : "Novo Mapeamento"}
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Rotulo (opcional)"
            size="small"
            value={form.label ?? ""}
            onChange={(e) => set("label", e.target.value)}
            fullWidth
          />

          <Divider />

          <FormControl size="small" fullWidth required>
            <InputLabel>Aba da planilha</InputLabel>
            <Select
              label="Aba da planilha"
              value={form.sheetName ?? ""}
              onChange={(e) => set("sheetName", e.target.value)}
            >
              {sheets.map((s) => (
                <MenuItem key={s.name} value={s.name}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth required>
            <InputLabel>Tipo de mapeamento</InputLabel>
            <Select
              label="Tipo de mapeamento"
              value={form.type ?? "cell"}
              onChange={(e) => handleTypeChange(e.target.value as MappingType)}
            >
              <MenuItem value="cell">Celula (valor unico)</MenuItem>
              <MenuItem value="column">Coluna (tabela)</MenuItem>
            </Select>
          </FormControl>

          {form.type === "cell" && (
            <TextField
              label="Referencia da celula (ex: C2)"
              size="small"
              required
              value={form.cellRef ?? ""}
              onChange={(e) => set("cellRef", e.target.value.toUpperCase())}
              fullWidth
            />
          )}

          {form.type === "column" && (
            <>
              <Box display="flex" gap={2}>
                <TextField
                  label="Cabecalho da coluna (texto exato na planilha)"
                  size="small"
                  required
                  value={form.columnHeader ?? ""}
                  onChange={(e) => set("columnHeader", e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Coluna (letra)"
                  size="small"
                  value={form.columnLetter ?? ""}
                  onChange={(e) =>
                    set("columnLetter", e.target.value.toUpperCase())
                  }
                  error={!letterIsValid || needsColumnLetter}
                  inputProps={{ maxLength: 3 }}
                  sx={{ width: 140, flexShrink: 0 }}
                />
              </Box>
              {repeatedHeaderLetters.length > 1 && (
                <Alert severity={needsColumnLetter ? "warning" : "info"}>
                  "{form.columnHeader?.trim()}" aparece nas colunas{" "}
                  {repeatedHeaderLetters.join(", ")} da linha {form.headerRow}.
                  Informe a letra da coluna que este mapeamento preenche.
                </Alert>
              )}
              <Box display="flex" gap={2}>
                <TextField
                  label="Linha do cabecalho"
                  size="small"
                  type="number"
                  value={form.headerRow ?? 1}
                  onChange={(e) =>
                    set("headerRow", parseInt(e.target.value, 10) || 1)
                  }
                  fullWidth
                />
                <TextField
                  label="Linha inicial dos dados"
                  size="small"
                  type="number"
                  value={form.startRow ?? 2}
                  onChange={(e) =>
                    set("startRow", parseInt(e.target.value, 10) || 2)
                  }
                  fullWidth
                />
              </Box>
            </>
          )}

          <Divider />

          <FormControl size="small" fullWidth required>
            <InputLabel>Origem dos dados</InputLabel>
            <Select
              label="Origem dos dados"
              value={form.source ?? "context"}
              onChange={(e) =>
                handleSourceChange(e.target.value as MappingSource)
              }
            >
              <MenuItem value="context">Contexto (campo unico)</MenuItem>
              <MenuItem value="arrayItem">Array (lista)</MenuItem>
            </Select>
          </FormControl>

          {form.source === "arrayItem" && (
            <FormControl size="small" fullWidth required>
              <InputLabel>Array de origem</InputLabel>
              <Select
                label="Array de origem"
                value={form.arrayField ?? ""}
                onChange={(e) => set("arrayField", e.target.value)}
              >
                {uniqueArrayFields.map((af) => (
                  <MenuItem key={af} value={af}>
                    {af}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {writesOnlyFirstItem && (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => handleTypeChange("column")}
                  style={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  Usar Coluna (tabela)
                </Button>
              }
            >
              Celula com origem Array escreve so o primeiro item da lista na
              celula informada. Para listar os servicos de um bloco, use o tipo
              Coluna (tabela) — um mapeamento por coluna.
            </Alert>
          )}

          <FormControl size="small" fullWidth required>
            <InputLabel>Campo do sistema (MM)</InputLabel>
            <Select
              label="Campo do sistema (MM)"
              value={form.ourField ?? ""}
              onChange={(e) => {
                const path = e.target.value;
                const field = availableFields.find((f) => f.path === path);
                setForm((prev) => ({
                  ...prev,
                  ourField: path,
                  category: fieldNeedsCategory(field)
                    ? prev.category
                    : undefined,
                }));
              }}
            >
              {filteredFields.map((f) => (
                <MenuItem key={f.path} value={f.path}>
                  {f.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {showValueStage && (
            <FormControl size="small" fullWidth>
              <InputLabel shrink>Valores da etapa</InputLabel>
              <Select
                label="Valores da etapa"
                notched
                displayEmpty
                value={form.valueStage ?? ""}
                onChange={(e) =>
                  set(
                    "valueStage",
                    (e.target.value || undefined) as
                      | MappingValueStage
                      | undefined
                  )
                }
              >
                <MenuItem value="">Etapa exportada (padrao)</MenuItem>
                {(
                  Object.keys(MAPPING_VALUE_STAGE_LABEL) as MappingValueStage[]
                ).map((stage) => (
                  <MenuItem key={stage} value={stage}>
                    {MAPPING_VALUE_STAGE_LABEL[stage]}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Para template com colunas por etapa (ex.: Orcamento inicial,
                Atualizado e Valor final). A coluna de uma etapa que o orcamento
                ainda nao alcancou na exportacao sai vazia.
              </FormHelperText>
            </FormControl>
          )}

          {showCategory && (
            <FormControl size="small" fullWidth required={categoryRequired}>
              <InputLabel shrink={hasGroupFilter || undefined}>
                Categoria de custo
              </InputLabel>
              <Select
                label="Categoria de custo"
                value={form.category ?? ""}
                displayEmpty={hasGroupFilter}
                notched={hasGroupFilter || undefined}
                onChange={(e) =>
                  set(
                    "category",
                    (e.target.value || undefined) as BudgetItemType | undefined
                  )
                }
                disabled={loadingCategories}
              >
                {hasGroupFilter && (
                  <MenuItem value="">
                    Todas as categorias do agrupamento
                  </MenuItem>
                )}
                {categoryOptions.map((opt) => (
                  <MenuItem key={opt.categoryKey} value={opt.categoryKey}>
                    {opt.categoryLabel}
                  </MenuItem>
                ))}
              </Select>
              {hasGroupFilter && (
                <FormHelperText>
                  Com filtro de agrupamento a categoria e opcional: sem ela, o
                  bloco traz os servicos de todas as categorias do agrupamento.
                </FormHelperText>
              )}
            </FormControl>
          )}

          {/*
            Recorte do bloco, nao um campo a escrever: o template do cliente
            pode ter uma secao por agrupamento dentro da mesma categoria, e sem
            isso todas recebem os mesmos servicos.
          */}
          {form.source === "arrayItem" && (
            <TextField
              label="Filtrar por agrupamento (opcional)"
              size="small"
              value={form.group ?? ""}
              onChange={(e) => set("group", e.target.value)}
              helperText="Deixe vazio para trazer todos os servicos da categoria. Preencha com o nome exato do agrupamento quando a planilha tiver uma secao por agrupamento."
              fullWidth
            />
          )}

          {repeatsFilter && (
            <Alert severity="error">{REPEATS_GROUP_FILTER_MESSAGE}</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} style={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!isValid() || (showCategory && loadingCategories)}
          variant="contained"
          style={{
            background: "#1361A4",
            color: "#fff",
            textTransform: "none",
          }}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
